import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { supabase } from './supabaseClient';

/** Custom scheme so an OAuth or password-reset redirect returns to this app. */
export const NATIVE_AUTH_SCHEME = 'io.mynewstart.app';

const APP_HOSTS = new Set(['mynewstart.io', 'www.mynewstart.io']);

export function isNativeApp(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * On the web, keep the caller's https redirect.
 * In the app, send the provider back to the custom scheme and remember the in-app path.
 */
export function authRedirectFor(redirectTo: string): string {
  if (!isNativeApp()) return redirectTo;
  const base = redirectTo.startsWith('http')
    ? new URL(redirectTo)
    : new URL(redirectTo, 'https://mynewstart.io');
  const callback = new URL(`${NATIVE_AUTH_SCHEME}://auth-callback`);
  callback.searchParams.set('next', `${base.pathname}${base.search}`);
  return callback.toString();
}

function safeNext(value: string | null): string {
  if (!value || !value.startsWith('/') || value.startsWith('//')) return '/home';
  return value;
}

async function closeAuthBrowser(): Promise<void> {
  try {
    await Browser.close();
  } catch {
    // The system browser is already closed when the redirect arrives.
  }
}

async function finishAuthUrl(url: string): Promise<void> {
  const parsed = new URL(url);
  const code = parsed.searchParams.get('code');
  if (code) {
    const { error } = await supabase.auth.exchangeCodeForSession(url);
    if (error) {
      window.location.assign(`/auth?error=${encodeURIComponent(error.message)}`);
      return;
    }
  }
  const next = safeNext(parsed.searchParams.get('next'));
  if (!code && (parsed.hash.includes('access_token') || parsed.hash.includes('error'))) {
    window.location.assign(`/auth${parsed.hash}`);
    return;
  }
  window.location.assign(next);
}

async function handleIncomingUrl(url: string): Promise<void> {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return;
  }
  if (parsed.protocol === `${NATIVE_AUTH_SCHEME}:` && parsed.hostname === 'auth-callback') {
    await closeAuthBrowser();
    await finishAuthUrl(url);
    return;
  }
  if (parsed.protocol === 'https:' && APP_HOSTS.has(parsed.hostname)) {
    if (parsed.searchParams.get('code')) {
      const { error } = await supabase.auth.exchangeCodeForSession(url);
      if (error) {
        window.location.assign(`/auth?error=${encodeURIComponent(error.message)}`);
        return;
      }
      parsed.searchParams.delete('code');
      parsed.searchParams.delete('state');
    }
    window.location.assign(`${parsed.pathname}${parsed.search}${parsed.hash}`);
  }
}

export async function initNativeShell(): Promise<void> {
  if (!isNativeApp()) return;
  document.documentElement.classList.add('native-app');
  document.documentElement.style.transform = '';

  if (Capacitor.getPlatform() === 'android') {
    await App.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) window.history.back();
      else void App.exitApp();
    });
  }

  try {
    await StatusBar.setOverlaysWebView({ overlay: false });
    await StatusBar.setBackgroundColor({ color: '#0CB6A9' });
    await StatusBar.setStyle({ style: Style.Light });
  } catch {
    // Status bar styling is unavailable on this platform.
  }

  await App.addListener('appUrlOpen', (event) => {
    void handleIncomingUrl(event.url);
  });
  const launch = await App.getLaunchUrl();
  if (launch?.url) await handleIncomingUrl(launch.url);
}
